import { useState, useEffect } from 'react';

/**
 * Base URL for the Laravel CMS API.
 * In development this points at the local Laravel backend (Valet / Herd).
 * Override with REACT_APP_CMS_API_URL env variable.
 */
import { CMS_API_URL } from '../config/apiBase';

/**
 * Fetch a CMS widget by brand slug and widget slug.
 *
 * @param {string} brandSlug  — brand identifier (e.g. 'abc')
 * @param {string} widgetSlug — widget slug (unique per brand)
 * @returns {{ data: object|null, loading: boolean, error: Error|null }}
 *
 * @example
 *   const { data, loading, error } = useWidget('abc', 'homepage-products');
 *   // data?.data?.items → array of product objects
 */
export default function useWidget(brandSlug, widgetSlug) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    if (!brandSlug || !widgetSlug) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();

    (async () => {
      setLoading(true);
      setError(null);

      try {
        const res = await fetch(
          `${CMS_API_URL}/api/widgets/${encodeURIComponent(brandSlug)}/${encodeURIComponent(widgetSlug)}`,
          { signal: controller.signal }
        );

        if (!res.ok) {
          throw new Error(`Widget API returned ${res.status}`);
        }

        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (err) {
        if (!cancelled && err.name !== 'AbortError') {
          console.error(`[useWidget] Failed to fetch widget "${brandSlug}/${widgetSlug}":`, err);
          setError(err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [brandSlug, widgetSlug]);

  return { data, loading, error };
}
